import React, {useState} from "react";
import crypto from "crypto-js";
import {format, formatDistanceToNow} from "date-fns";
import {getReadableString} from "constraints/general";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from "@material-ui/core";
import {ShowDateFormat, TaskStatus} from "@evennt-tech/api-v2-core";
import type {IListEmailReviewAllTasksItem} from "@evennt-tech/api-v2-external";
import {differenceDays} from "tools/task/tasksStatuses";
import CheckIcon from "view/images/CheckInCircleIcon";
import EmptyTaskStatusIcon from "view/images/EmptyTaskStatusIcon";
import CircleIcon from "@material-ui/icons/RadioButtonUnchecked";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {generateTestId} from "tools/cypress/generateTestId";
import {EMAIL_TASK_REVIEW_PATH} from "view/routes";
import useReviewAllShowsTasks from "./useReviewAllShowsTasks";
import useStyles from "./styles";

// Public page — don't use auth/frontend hooks here; all data comes from the BE.
const AllTaskReview = () => {
  const classes = useStyles();
  const params = new URLSearchParams(window.location.search);
  const [showCompleted, setShowCompleted] = useState<boolean>(false);
  const saltKey = import.meta.env.VITE_EMAIL_SALT as string;
  const key = params.get("key");

  if (!key) {
    return null;
  }

  const [data, isLoading] = useReviewAllShowsTasks(key, showCompleted);
  const items = data?.items ?? [];
  const reviewerEmail = data?.reviewerEmail ?? "";

  // The per-task EmailTaskReview page expects an AES-encrypted reviewer
  // URL param, so re-encrypt the plain email before linking through.
  const encryptedReviewer = reviewerEmail
    ? crypto.AES.encrypt(reviewerEmail, saltKey).toString()
    : "";

  const renderIcon = (item: IListEmailReviewAllTasksItem<Date>) => {
    const dueDate = item.dueDate ? new Date(item.dueDate) : null;
    const now = Date.now();
    const isWaiting =
      item.status === TaskStatus.WAITING_FOR_APPROVAL ||
      item.status === TaskStatus.IN_REVIEW;
    if (item.status === TaskStatus.DONE) {
      return <CheckIcon className={classes.check} />;
    }
    if (isWaiting && dueDate && dueDate.getTime() < now) {
      return <CircleIcon className={classes.overdue} />;
    }
    if (isWaiting && dueDate && differenceDays(dueDate.getTime(), now) < 5) {
      return <CircleIcon className={classes.waitingAndWarningTodo} />;
    }
    if (isWaiting) {
      return <CircleIcon className={classes.waitingAndWarningTodo} />;
    }
    return <CircleIcon className={classes.todo} />;
  };

  const renderUpdatedStatus = (editCount: number) => {
    if (!editCount) {
      return null;
    }
    return (
      <Typography className={classes.taskUpdatedStatus}>Updated</Typography>
    );
  };

  /**
   * A submitted multitask whose sub-task answers moved on since that submit.
   * Server-computed (`needsResubmit`), the same field and rule the organiser's
   * own list reads — so this reviewer queue can't disagree with the task page
   * it links to. Reviewer-side wording ("Contains updates"), never the
   * exhibitor's "Resubmission required": nobody here owes the resubmit.
   */
  const renderContainsUpdates = (needsResubmit: boolean) => {
    if (!needsResubmit) {
      return null;
    }
    return (
      <Box
        className={classes.containsUpdatesChip}
        {...generateTestId("all-task-review-contains-updates")}
      >
        <InfoOutlinedIcon className={classes.containsUpdatesIcon} />
        Contains updates
      </Box>
    );
  };

  const renderReviews = () => {
    if (!data || isLoading) {
      return (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={35} className={classes.loading} />
        </Box>
      );
    }

    // With "Show completed" off the BE drops DONE instances, so a
    // caught-up reviewer lands here; nudge them toward the toggle.
    if (!items.length) {
      return (
        <Box className={classes.emptyState}>
          <EmptyTaskStatusIcon />
          <Typography className={classes.emptyTitle}>
            {showCompleted ? "No tasks to review" : "You're all caught up"}
          </Typography>
          <Typography className={classes.emptySubtitle}>
            {showCompleted
              ? "There are no tasks assigned to you for review."
              : "No tasks are waiting for your review right now. Tick “Show completed” to see tasks you've already reviewed."}
          </Typography>
        </Box>
      );
    }

    // Group the flat item list by show, preserving first-seen order. The BE
    // returns `shows` as a lookup (id → name/dateFormat); each item carries its
    // own `showId`, so the reviewer's whole cross-show queue renders as one
    // section per show.
    const showById = new Map(data.shows.map((entry) => [entry.id, entry]));
    const groups: {
      show: {id: string; name: string; dateFormat: ShowDateFormat | null};
      items: IListEmailReviewAllTasksItem<Date>[];
    }[] = [];
    const groupIndexByShow = new Map<string, number>();
    for (const item of items) {
      const show = showById.get(item.showId);
      if (!show) continue;
      let groupIndex = groupIndexByShow.get(item.showId);
      if (groupIndex === undefined) {
        groupIndex = groups.length;
        groupIndexByShow.set(item.showId, groupIndex);
        groups.push({show, items: []});
      }
      groups[groupIndex].items.push(item);
    }

    if (!groups.length) {
      return null;
    }

    return (
      <Box>
        {groups.map((group) => {
          const {show} = group;
          const dateFormat = show.dateFormat ?? undefined;
          return (
            <Box key={show.id}>
              <Box className={classes.showTitle} mb={2} mt={2}>
                {show.name}
              </Box>
              <Box className={classes.taskListContainer}>
                {group.items.map((item, index) => {
                  const hideBorder = index === group.items.length - 1;
                  const lastActivity = item.lastActivityAt
                    ? new Date(item.lastActivityAt)
                    : null;
                  const dueFormat = item.dueDate
                    ? format(
                        new Date(item.dueDate),
                        getReadableString({dateFormat})
                      )
                    : "";
                  const updateTime = lastActivity
                    ? formatDistanceToNow(lastActivity, {addSuffix: true})
                    : "";
                  return (
                    <Grid
                      key={item.taskInstanceId}
                      onClick={() => {
                        const params = new URLSearchParams({
                          showId: item.showId,
                          taskId: item.taskId,
                          standId: item.standId,
                          key,
                        });
                        if (item.companyToShowId) {
                          params.set("companyId", item.companyToShowId);
                        }
                        if (encryptedReviewer) {
                          params.set("reviewer", encryptedReviewer);
                        }
                        window.open(
                          `${
                            window.location.origin
                          }${EMAIL_TASK_REVIEW_PATH}?${params.toString()}`,
                          "_self"
                        );
                      }}
                      container
                      spacing={0}
                      className={classes.taskRow}
                    >
                      <Grid item xs={1} className={classes.iconCell}>
                        {renderIcon(item)}
                      </Grid>
                      <Grid
                        item
                        xs={9}
                        className={classes.mainCell}
                        style={{
                          borderBottom: hideBorder ? "" : "#e0e0e0 1px solid",
                        }}
                      >
                        {/* Title row, not the chips column: the label sits
                            right after the task name the same way it does in
                            the organiser's own list. */}
                        <Box display="flex" alignItems="center">
                          {/* `minWidth: 0` so the title, not the label, is what
                              ellipsises when the row is too narrow. */}
                          <Box className={classes.taskTitle} minWidth={0}>
                            {item.title}
                          </Box>
                          {renderContainsUpdates(item.needsResubmit)}
                        </Box>
                        {/* Render only when populated to avoid an empty bullet. */}
                        {(item.companyName || item.standName) && (
                          <Box className={classes.companyLine}>
                            {[item.companyName, item.standName]
                              .filter(Boolean)
                              .join(" • ")}
                          </Box>
                        )}
                        <Box className={classes.bottomLine}>
                          {updateTime} by {item.respondentName ?? ""}, due{" "}
                          {dueFormat} - approval by {reviewerEmail}
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={2}
                        className={classes.rightChipsColumn}
                        style={{
                          borderBottom: hideBorder ? "" : "#e0e0e0 1px solid",
                        }}
                      >
                        {/* This reviewer's own verdict, if they already decided. */}
                        {item.isReviewerApproved && (
                          <Typography className={classes.approvedChip}>
                            Approved
                          </Typography>
                        )}
                        {item.isReviewerRejected && (
                          <Typography className={classes.rejectedChip}>
                            Rejected
                          </Typography>
                        )}
                        {renderUpdatedStatus(item.editCount)}
                      </Grid>
                    </Grid>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box className={classes.card}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Task Review</Typography>
        <FormControlLabel
          className={classes.checkbox}
          label="Show completed"
          control={
            <Checkbox
              style={{color: "#757575"}}
              value={showCompleted}
              onChange={() => setShowCompleted(!showCompleted)}
              color="primary"
            />
          }
        />
      </Box>
      {renderReviews()}
    </Box>
  );
};

export default AllTaskReview;
